import React, { useEffect, useState } from "react";
import { callApi, HttpMethod } from "src/services/apiService";

interface FormulaVariable {
  name: string;
  description: string;
}

interface FormulaVariablesPanelProps {
  modelId: number;
}

const FormulaVariablesPanel: React.FC<FormulaVariablesPanelProps> = ({ modelId }) => {
  const [variables, setVariables] = useState<FormulaVariable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(true);
  const [copiedName, setCopiedName] = useState<string | null>(null);

  const copyToClipboard = async (name: string) => {
    await navigator.clipboard.writeText(name);
    setCopiedName(name);
    setTimeout(() => setCopiedName(null), 1500);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await callApi(`sysadmin/product-pricing-models/${modelId}/formula-variables`, HttpMethod.GET);
        if (!cancelled) setVariables(data);
      } catch {
        if (!cancelled) setError("Failed to load formula variables.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [modelId]);

  return (
    <div style={{ position: "relative" }}>
      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setCollapsed((c) => !c)}
        type="button"
      >
        <i className={`bi bi-chevron-${collapsed ? "right" : "down"} me-1`} />
        Formula Variables Reference
      </button>
      {!collapsed && (
        <div className="border rounded p-2 mt-1 bg-white shadow" style={{ position: "absolute", zIndex: 10, minWidth: 900, maxHeight: "80vh", overflowY: "auto", fontSize: "0.85rem" }}>
          {loading && <span className="text-muted">Loading…</span>}
          {error && <span className="text-danger">{error}</span>}
          {!loading && !error && (
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Variable</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {variables.map((v) => (
                  <tr key={v.name}>
                    <td>
                      <code
                        style={{ cursor: "pointer" }}
                        title="Click to copy"
                        onClick={() => copyToClipboard(v.name)}
                      >
                        {v.name}
                      </code>
                      {copiedName === v.name && <span className="text-success ms-1" style={{ fontSize: "0.75rem" }}>Copied!</span>}
                    </td>
                    <td>{v.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default FormulaVariablesPanel;
